import React, { useState, useEffect } from "react";
import { RouteChildrenProps, useHistory, useParams } from "react-router-dom";
import regbanner from "../../../assets/icons/progresslogo.png";
import errorIcon from "../../../assets/icons/errorIcon.png";
import "./ElogVerificationInProgress.scss";
import Button from "../../UI/Button/Button";
import { AGetStageCompletionStatus } from "../../Services/registration/continueApiService";
import GlobalStore from "../../store/globalStore";
import {
  ADionPostData,
  AElogGetAuthUrl,
  AElogUpdateStatus,
} from "../../Services/registration/elogApiService";
import { decryptJwtToken } from "../../Services/dashboard/encryptionNew";
import { ITokenObj } from "../../types/genericTypes";
import { containerUrl } from "../../utils/constants";
import useQuery from "../../../customHooks/useQuery";

const ElogVerificationInProgress: React.FC<RouteChildrenProps> = () => {
  const history = useHistory()
  const rawSearch = window.location.search;
  // "?id=NDUyNzkwNDA=?STATUS=SUCCESS&elgstatus=111"

  // remove leading "?"
  const query = rawSearch.substring(1);
  // "id=NDUyNzkwNDA=?STATUS=SUCCESS&elgstatus=111"

  let appId: string | null = null;
  let status: string | null = null;
  let elgstatus: string | null = null;

  if (query) {
    // Special fix: split id part at "=?STATUS"
    const [idPart, rest] = query.split("=?STATUS");

    const appIdEnc = idPart.replace("id=", ""); // "NDUyNzkwNDA"
    status = "SUCCESS"; // always SUCCESS in this broken format

    // get elgstatus from rest
    const params = new URLSearchParams("STATUS=SUCCESS" + rest);
    elgstatus = params.get("elgstatus");

    // decode appId safely
    try {
      const base64Regex = /^[A-Za-z0-9+/=]+$/;
      if (base64Regex.test(appIdEnc)) {
        appId = atob(appIdEnc);
      } else {
        appId = appIdEnc; // fallback
      }
    } catch {
      appId = appIdEnc; // fallback
    }
  }

  const [failed, setFailed] = useState(false);
  const [authUrlFailed, setAuthUrlFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (appId) {
      GlobalStore.applicationID = appId + "I"
      AElogUpdateStatus(
        (updateRes: any) => {
          console.log("Elog update status response (from redirect):", updateRes);
          setIsLoading(false);
          if (updateRes?.data) {
            history.push(containerUrl.esignsubmit);
          } else {
          }
        },
        {
          applicationId: appId,
          isElogOpted: true,
          status: status || "FAILED",
          statusCode: elgstatus || "500",
          isElogApiResponse: true,
        }
      );
    } else {
      // ✅ Normal flow (no id in URL)
      AGetStageCompletionStatus(
        (res: any) => getClientStageDetails(res),
        GlobalStore.applicationID
      );
    }
  }, [appId]);


  function getClientStageDetails(res: any) {
    if (!res || res.error) {
      console.log("Stage completion error", res?.error || res?.errMsg);
      // setFailed(true);
      setIsLoading(false);
      return;
    }

    if (res.data) {
      try {
        const responseObj = JSON.parse(res.data);

        if (responseObj?.isdioncompleted && responseObj?.iselogcompleted) {
          history.push(containerUrl.esignsubmit);
          return;
        }

        if (responseObj?.isdioncompleted && !responseObj?.iselogcompleted) {
          // Continue directly with E-Log auth
          AElogGetAuthUrl((elogRes: any) => {
            console.log("Dion already completed, skipping Dion API", elogRes);
            if (!elogRes || elogRes.error) {
              console.error("Elog Auth failed:", elogRes);
              // setFailed(true);
              setIsLoading(false);
              setAuthUrlFailed(true)
              return;
            }

            const url = elogRes?.data?.Authurl;
            const errorDesc = elogRes?.data?.ErrorDesc || "";

            if (url) {
              window.alert("You will be redirected for E-Log verification");
              window.location.href = url;
            } else {
              if (errorDesc.includes("AUTHENTICATION IS ALREADY DONE")) {
                // ✅ Call AElogUpdateStatus instead of setting failed
                AElogUpdateStatus(
                  (updateRes: any) => {
                    setIsLoading(false);
                    if (updateRes?.data) {
                      history.push(containerUrl.esignsubmit);
                    }
                  },
                  {
                    applicationId: GlobalStore.applicationID,
                    isElogOpted: true,
                    status: "SUCCESS",
                    statusCode: "101",
                    isElogApiResponse: true,
                  }
                );
              } else {
                // ✅ Fallback for other errors
                setAuthUrlFailed(true);
                setIsLoading(false);
              }
            }

          }, GlobalStore.applicationID);

          return;
        }

        // ✅ Normal flow (if not completed yet)
        if (responseObj?.elog_opted) {
          let userId = 0;
          const tokenObj: string = decryptJwtToken(GlobalStore?.token);
          if (tokenObj !== "" && !!tokenObj && tokenObj?.length > 0) {
            let tokenMainObj: ITokenObj = JSON.parse(tokenObj) as ITokenObj;
            userId = parseInt(tokenMainObj?.UserId?.toString());
          }

          const dionPayload = {
            accountCategory: responseObj.accountcategory,
            applicationId: GlobalStore.applicationID,
            stage: "ELOG_VERIFICATION",
            userId: userId,
          };

          // Step 2: Dion API
          // helper: delay
          const delay = (ms: any) => new Promise((resolve) => setTimeout(resolve, ms));

          // helper: Promisify AElogGetAuthUrl
          const getElogAuthUrl = (applicationId: any) => {
            return new Promise((resolve, reject) => {
              AElogGetAuthUrl((res: any) => {
                if (!res || res.error) {
                  reject(res);
                } else {
                  resolve(res);
                }
              }, applicationId);
            });
          };

          // main flow
          ADionPostData(
            async (dionRes: any) => {
              if (!dionRes || dionRes.error) {
                console.error("Dion failed:", dionRes);
                setTimeout(() => {
                  setFailed(true);
                  setIsLoading(false);
                }, 1000);
                return;
              }

              console.log("Dion success:", dionRes);

              // Retry delays (in milliseconds)
              const retryDelays = [5000, 7000, 10000];
              let success = false;

              for (const delayMs of retryDelays) {
                await delay(delayMs);
                console.log(`⏳ Retrying AElogGetAuthUrl after ${delayMs / 1000}s...`);
                try {
                  const elogRes: any = await getElogAuthUrl(GlobalStore.applicationID);

                  const errorDesc = elogRes?.data?.ErrorDesc || "";
                  const url = elogRes?.data?.Authurl;

                  if (!url && !errorDesc.includes("AUTHENTICATION IS ALREADY DONE")) {
                    console.error("Elog Auth failed:", elogRes);
                    continue;
                  }


                  if (url) {
                    window.alert("You will be redirected for E-Log verification");
                    window.location.href = url;
                    success = true;
                    break;
                  } else if (errorDesc.includes("AUTHENTICATION IS ALREADY DONE")) {
                    // ✅ Call AElogUpdateStatus instead of setting failed
                    AElogUpdateStatus(
                      (updateRes: any) => {
                        setIsLoading(false);
                        if (updateRes?.data) {
                          history.push(containerUrl.esignsubmit);
                        }
                      },
                      {
                        applicationId: GlobalStore.applicationID,
                        isElogOpted: true,
                        status: "SUCCESS",
                        statusCode: "101",
                        isElogApiResponse: true,
                      }
                    );
                    success = true;
                    break;
                  } else {
                    console.warn("Elog Auth returned no URL. Will retry...");
                  }
                } catch (err) {
                  console.error("Elog Auth exception:", err);
                }
              }

              if (!success) {
                console.error("Elog Auth failed after all retries");
                setAuthUrlFailed(true);
                setIsLoading(false);
              }
            },
            dionPayload
          );

        } else {
          // setFailed(true);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Parsing error", err);
        // setErrCode("ERR_PARSE");
        // setFailed(true);
        setIsLoading(false);
      }
    } else {
      // setFailed(true);
      setIsLoading(false);
    }
  }

  const handleRetry = () => {
    setFailed(false);
    setIsLoading(true);

    // re-trigger flow
    AGetStageCompletionStatus(
      (res: any) => getClientStageDetails(res),
      GlobalStore.applicationID
    );
  };

  if (authUrlFailed) {
    return (
      <div className="docs-pending-root">
        <img src={errorIcon} alt="Error" className="verified-img" />
        <h2 className="status-heading">Unable to Proceed</h2>
        <span className="status-subHead">
          {"We’re experiencing technical difficulties. Please contact your RM for assistance."}
        </span>
      </div>
    );
  }
  if (failed) {
    return (
      <div className="docs-pending-root">
        <img src={errorIcon} alt="Error" className="verified-img" />
        <h2 className="status-heading">Unable to Proceed</h2>
        <span className="status-subHead">
          {"We’re experiencing technical difficulties. Please contact your RM for assistance."}
        </span>
        {/* <div className="retry-btn-container">
          <Button btnName="Try Again" onClick={handleRetry} />
        </div> */}
      </div>
    );
  }

  return (
    <div className="docs-pending-root">
      <img src={regbanner} className="verified-img" />
      <div>
        <h2 className="status-heading">
          {isLoading && "Verification in Progress"}
        </h2>
        <span className="status-subHead">
          {isLoading && "You will be redirected to BSE Star MF for E-Log authentication. Please complete the process to open your account instantly."}
        </span>
      </div>
    </div>
  );
};

export default ElogVerificationInProgress;